import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
// Authentication layout components
import IllustrationLayout from "../components";
import { useAuth0 } from "@auth0/auth0-react";
import useSocketUserDisconnect from "hooks/useSocketUserDisconnect";
import SoftBox from "components/SoftBox";

import { resendVerificationEmail } from "api/Auth0Api";
import { THEME_COLORS } from "constants/theme";
import { getEnv } from "helpers/environment";

function Verification() {
  const { logout, user } = useAuth0(); // Get the logout function from Auth0
  const { themeMode } = useSelector((store) => store?.portal?.settings);
  const { userDisconnect } = useSocketUserDisconnect();
  const [timeoutCount, setTimeoutCount] = useState(null); // 60 seconds

  const currentUrl = window.location.protocol + "//" + window.location.host;

  useEffect(() => {
    const timeout = setTimeout(() => {
      userDisconnect();
      logout({
        logoutParams: {
          returnTo: currentUrl + "/authentication/sign-in"
        }
      });
    }, 300000); // 5 minutes = 300,000 milliseconds
    // 20 sec = 20000

    return () => {
      clearTimeout(timeout); // Clear the timeout if the component unmounts before the timeout is reached
    };
  }, [logout]);

  const handleResendVerificationEmail = async () => {
    setTimeoutCount(getEnv("RESEND_EMAIL_VERIFICATION_TIMEOUT"));
    const resendVerificationEmailResponse = await resendVerificationEmail(user?.sub);
    if (resendVerificationEmailResponse?.isSuccessful) {
      toast.success(resendVerificationEmailResponse?.message);
    } else {
      toast.error(resendVerificationEmailResponse?.message);
    }
  };

  const handleSignInWithDifferentAccount = () => {
    logout({
      logoutParams: {
        returnTo: currentUrl + "/authentication/sign-in"
      }
    });
  };

  useEffect(() => {
    if (timeoutCount !== null) {
      localStorage.setItem("timeoutCount", timeoutCount);
      const resendVerificationEmailInterval = setInterval(() => {
        setTimeoutCount((prev) => {
          if (prev === 1) {
            clearInterval(resendVerificationEmailInterval);
            localStorage.removeItem("timeoutCount");
            return null;
          } else {
            return prev - 1;
          }
        });
      }, 1000);

      return () => {
        clearInterval(resendVerificationEmailInterval);
      };
    }
  }, [timeoutCount]);

  useEffect(() => {
    const storedTimeoutCount = localStorage.getItem("timeoutCount");
    if (storedTimeoutCount !== null) {
      // If there is a stored timeout count, set it in the component's state
      setTimeoutCount(Number(storedTimeoutCount));
    }

    // Function to handle changes to localStorage
    const handleStorageChange = (event) => {
      // Check if the changed key is "timeoutCount"
      if (event.key === "timeoutCount") {
        // Get the new value of "timeoutCount" from localStorage
        const newTimeoutCount = event.newValue ? Number(event.newValue) : null;
        // Update the component's state with the new timeout count
        setTimeoutCount(newTimeoutCount);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    return () => {
      // Clear localStorage and reset state when the component unmounts
      localStorage.removeItem("timeoutCount");
      setTimeoutCount(null);
    };
  }, []);

  return (
    <IllustrationLayout
      title="Waiting for email verification"
      description="Please check your email for the verification link"
      // forButton="Back to sign in"
    >
      <div id="fff" style={{ display: "none" }}></div>

      <div id="fffd" style={{ position: "relative", top: "-50px" }}></div>

      <SoftBox display="flex" flexDirection="column" alignItems="center" gap={1} mt={2}>
        <SoftButton
          color="primary"
          sx={{ mx: "auto" }}
          onClick={handleResendVerificationEmail}
          disabled={timeoutCount !== null}
        >
          Resend verification email {timeoutCount}
        </SoftButton>
        <SoftTypography variant="body2" fontWeight="regular" color="text">
          or
        </SoftTypography>
        <SoftBox>
          <SoftTypography variant="button" color="text" fontWeight="regular">
            <SoftTypography
              onClick={handleSignInWithDifferentAccount}
              variant="button"
              fontWeight="medium"
              sx={{
                cursor: "pointer",
                color: THEME_COLORS[themeMode.toUpperCase()].PRIMARY_FONT,
                textDecoration: "underline",
                "&:hover": { textDecoration: "none" }
              }}
              className="signUpTitle"
            >
              Sign in with a different account
            </SoftTypography>
          </SoftTypography>
        </SoftBox>
      </SoftBox>
    </IllustrationLayout>
  );
}

export default Verification;
